/**
 * THE COLOUR AUTHORITY.  Owned by `look` (ARCHITECTURE.md §3, §10).
 *
 * Nobody else hardcodes a colour anywhere in this codebase. If you need a colour, import it
 * from here or ask `look` to add one.
 *
 * PROVENANCE
 * ----------
 * Every value marked `[S]` was sampled with ImageMagick from a real file in `reference/`
 * (see `reference/palette/palette.md` for the exact crop coordinates). Values marked `[D]`
 * are derived ramps built from those anchors — interpolations, shade/lit variants, and
 * value-separation targets. Nothing here is invented from memory.
 *
 * THE THREE RULES THIS PALETTE ENFORCES
 * -------------------------------------
 * 1. VALUE SEPARATION BEFORE DETAIL.  Road is the brightest large surface in frame
 *    (L* ~66). Buildings sit below it (L* 40-58). Cast shadow is genuinely dark (L* ~13).
 *    A fresh patch punched into the road is a 5.4:1 luminance ratio against it — that gap
 *    IS the PowerWash before/after payoff.
 * 2. CONFIDENT CHROMA, NOT MUD.  Crazy Taxi's sky is a real blue (#3473FB), its grass is a
 *    real green. We keep that. What we do NOT do is oversaturate Montreal's masonry —
 *    unpainted brick sampled at S≈0.25, a warm greyed brown, not fire-engine red.
 * 3. CONES ARE THE HIGHEST-CONTRAST OBJECT IN FRAME.  Orange alone is only 1.9:1 against
 *    lit asphalt. The white reflective bands are what make a cone findable at 90 km/h.
 *    They are not decoration. Never drop them.
 *
 * FORMAT
 * ------
 * All values are sRGB hex integers. Pass them straight to `THREE.Color` / `material.color`
 * — three's ColorManagement is enabled, so a hex literal is interpreted as sRGB and
 * converted into the linear working space for you.
 */

import * as THREE from 'three';

/* ══════════════════════════════════════════════════════════════════════════════════════
   1. SKY  — one set per weather. `look` cross-fades between them.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const SKY = Object.freeze({
  /** Bright spring afternoon. The Crazy Taxi identity colour. Do not let tonemapping eat it. */
  thaw: Object.freeze({
    zenith: 0x2A62EE,     // [S] ct-t456 x380 y90 (#3473FB) pushed 1 step deeper for gradient headroom
    mid: 0x4582FB,        // [S] ct-t456 x600 y90
    horizon: 0x8DC3FC,    // [D] between sampled #65AAFB and the haze band
    haze: 0xC6E2F7,       // [D] the pale band right on the horizon line
    ground: 0x7E8B93,     // [D] below-horizon fill (never seen except on hills)
    cloud: 0xF4FAFD,      // [S] ct-t396 x760 y130 (#CFF6FD) lifted — CRT capture darkens whites
    cloudShade: 0xA8C4DE, // [D] underside of the cumulus
    sun: 0xFFF3D2,        // [D] sun disc core
    sunHalo: 0xFFE7A8,    // [D] the bloom ring around it
  }),

  /** Montreal whiteout. Sky and ground very nearly merge; max saturation across frame ≈ 0.11. */
  /**
   * Rubric `winter-slush` crit 4: the sky must be BRIGHTER THAN OR EQUAL TO the lit snow
   * (#F5F6F7 or above). Round 3 measured #E4ECF5 (236) at the top of frame against that 245
   * floor, and the darkened-zenith ramp was why. A Montreal whiteout has no visible zenith —
   * sky and snow merge, which is the whole point of the weather.
   */
  winter: Object.freeze({
    zenith: 0xDCE1E6,     // [D] whiteout: barely darker than the horizon
    mid: 0xE9ECEF,        // [D]
    horizon: 0xF4F6F7,    // [S] winter/Montreal_-_Plateau__day_of_snow (#F5F6F7)
    haze: 0xF8F9F9,       // [S] the whiteout band where sky meets snow
    ground: 0xD8DDE1,     // [D]
    cloud: 0xC9D2D9,      // [D] overcast has no cloud shapes, only tonal drift
    cloudShade: 0x9AA6B1, // [D]
    sun: 0xE6E4DC,        // [D] a bright smudge, never a disc
    sunHalo: 0xD3D6D8,    // [D]
  }),

  /**
   * Night + rain. Reference §9.5 is unambiguous: the unlit Montreal night sky samples
   * #241D16 — a WARM dark brown-black, because it is overcast and lit from below by the
   * city's own high-pressure sodium. Round 3 shipped a blue-dominant dome (zenith #0A0D16,
   * mid #141A26) that read as crushed blue-black and failed criterion 2 outright. The whole
   * dome is now lit to the sampled warm value; only the very top loses a little of it.
   */
  nightrain: Object.freeze({
    zenith: 0x18130E,     // [D] from the sampled #241D16, one step down for gradient headroom
    mid: 0x241D16,        // [S] montreal/Alley__Vieux-Montreal — unlit sky
    horizon: 0x453728,    // [D] sodium spill bouncing off the cloud base
    haze: 0x483A22,       // [S] derived from #463619 alley asphalt-near-lamp bounce
    ground: 0x1A150F,     // [D]
    cloud: 0x413628,      // [D] lit cloud belly
    cloudShade: 0x241E18, // [D]
    sun: 0x2E2A22,        // [D] moon — a smudge behind cloud, never a blue disc
    sunHalo: 0x241E16,    // [D]
  }),
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   2. SUN / LIGHT RIG
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const LIGHT = Object.freeze({
  /** Direct sun colour per weather. */
  sun: Object.freeze({
    // Crazy Taxi's key light is essentially WHITE. #FFF2D6 has R-B = +41, and with the sun
    // carrying ~55% of a lit surface it dragged the sampled road from the reference's
    // neutral-to-green #A8AFAA to a warm #776F65 (R-B = +19). Keep the warmth in the bounce.
    thaw: 0xFFFAF0,       // [D] near-white high spring sun
    winter: 0xE8EEF4,     // [D] cold diffuse — overcast has no true sun
    nightrain: 0x9C8F76,  // [D] skyglow key, very weak. NOT a blue moon: reference §9.1 —
                          //     every Montreal night frame is sodium, not moonlight.
  }),
  /** Hemisphere sky term. */
  hemiSky: Object.freeze({
    // The hemisphere sky term IS the cast-shadow colour for every up-facing surface, so it
    // is the single thing that decides whether a shadow on the road reads as #878E89 (the
    // reference) or as navy. It must be NEUTRAL, faintly green to sit in the #A8AFAA family.
    thaw: 0xE4E8E4,       // [D]
    // De-blued. Lit snow measured B-R = +27 against the sampled #E0E4E6 family's +6, and
    // the hemisphere sky term is what every up-facing snow surface is made of.
    winter: 0xDDE2E5,     // [D]
    // WARM. This is the term that decides what an unlit night surface bottoms out at, and
    // it is what stops the blue channel clipping to zero across most of the frame.
    nightrain: 0x33281B,  // [D]
  }),
  /** Hemisphere ground term — what bounces UP off the street. */
  hemiGround: Object.freeze({
    // THIS IS THE FACADE-BRIGHTNESS CONTROL, AND ONLY THE FACADE-BRIGHTNESS CONTROL.
    //
    // three's hemisphere term is mix(ground, sky, 0.5*dot(N,up)+0.5), so a horizontal road
    // sees the SKY colour and nothing else, while a vertical wall sees a 50/50 blend. Raising
    // this lifts every facade in the city and moves the road by exactly zero — which is what
    // the round-3 note asked for ("lift the street wall without touching the road"). The old
    // #A69C8E left the street wall at half the §3.2 floor; #CDC3B4 puts the hemisphere term
    // on a vertical face up 52%, and it is physically the honest value anyway: the thing
    // bouncing up off the street is the brightest large surface in the frame.
    thaw: 0xCDC3B4,       // [D] warm asphalt/sidewalk bounce — the street wall's main light
    winter: 0xC4CBD1,     // [D] snow bounce, very strong in winter
    nightrain: 0x54401E,  // [S-derived] #6B4A18 sodium deep falloff
  }),
  /** Opposite-side bounce fill so shadowed faces are readable, never black. */
  fill: Object.freeze({
    thaw: 0xEAECE8,       // near-neutral: the fill exists to keep shadow sides
    winter: 0xE8ECEE,     // readable, not to tint them. A blue fill turns every cast shadow
    nightrain: 0x382E21,  // navy; the sampled Crazy Taxi cast shadow is #222823, a NEUTRAL.
  }),
  /** Street-lamp sodium — used for the night rim/haze term and lamp emissives. */
  sodiumCore: 0xF4E3BF,   // [S] montreal/Rue_Saint-Denis_Montreal_08 lamp halo core
  sodiumMid: 0xC89F62,    // [S] sodium landing on stone
  sodiumFall: 0x946F36,   // [S] sodium falloff
  sodiumDeep: 0x6B4A18,   // [S] sodium deep falloff
  /**
   * What a sodium FIXTURE is tinted, as opposed to what its halo measures.
   *
   * `sodiumCore` is the halo right under the lamp, which is nearly white because it is
   * blown out; using it as the light's own colour puts near-neutral light on everything in
   * the pool and the surface keeps its own grey. Reference §9.1's defining property is the
   * opposite — "the lit surfaces LOSE their own hue", greystone under sodium samples
   * #C2994F. So the LIGHT is authored at the mid-ramp and the near-white core falls out of
   * the tonemap's highlight compression, not out of the source.
   */
  sodiumLamp: 0xF0C182,   // [D] derived from sodiumCore/sodiumMid
  /**
   * The cooler modern LED fixture on the arterials — reference §9.2. Still warm-biased (the
   * road under it samples #6E644F, not a neutral grey) but far less amber than sodium.
   * NOT CURRENTLY LIT BY A LIGHT: a second punctual light costs the whole frame budget
   * (see LOOK_TUNING.light.lamp). It is here for whoever adds the cool zone as an emissive
   * or a ground decal, which is the only affordable way to get `night-rain` criterion 7.
   */
  ledCool: 0xE6E9E2,      // [S] montreal/Rue_Saint-Denis_Montreal_02 — the whiter fixtures
  /**
   * The modern LED head as a SOURCE, for the ground pools in `render/lamps.js`.
   *
   * `ledCool` above is the fixture's own body colour as photographed — near-neutral, because
   * it is blown out. §9.2's `#6E644F` is the other end: that is this source AFTER it lands on
   * warm asphalt at 8-12 m, where it is nearly out of energy. Neither of those is what to
   * feed an additive pool with. A 4500-5000 K head sitting over a near-black wet road reads
   * distinctly blue-white, and that contrast against the sodium on the side streets is what
   * `night-rain` criterion 7 asks to see — plus it is the only honest cool zone available in
   * a frame the round-8 critic measured at 90.2% warm against 1.1% cool.
   */
  ledPool: 0x93C0EE,      // [D] 4700 K head, derived from ledCool by removing the sodium bias
                          //     and saturated to S 0.38 so it survives the "colourful lit
                          //     pixel" test that S 0.19 silently failed (see LIGHT notes).
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   3. ROAD / ASPHALT  — the value backbone of the whole game
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const ROAD = Object.freeze({
  asphaltLit: 0xA0A0A1,       // [S] potholes/Nasty_pothole_in_the_middle_of_Virginia_Avenue — L* 66
  asphaltMid: 0x8E8A85,       // [S] montreal/Rue_Saint-Denis
  asphaltShade: 0x646465,     // [S] same photo, shaded side
  asphaltDeep: 0x4A4A4B,      // [D] under-vehicle contact shade
  aggregate: 0xDBDBDC,        // [S] exposed aggregate speckle highlight
  aggregateDark: 0x5B5A58,    // [D] the dark half of the speckle
  tarSeam: 0x2A2827,          // [S] MassDOT I-93 crack-seal seam
  saltBloom: 0xD3D0D3,        // [S] potholes/Pothole_in_Villeray__Montréal — winter salt haze
  oilStain: 0x3B3937,         // [D] centre-of-lane drip staining
  ruelleLit: 0xA09C95,        // [S] montreal/Ruelle_008 — alleys are warmer + browner
  ruelleShade: 0x6C6965,      // [S] same
});

/** The patch ramp. Fresh → cured is the PowerWash before/after and must stay this wide. */
export const PATCH = Object.freeze({
  freshWet: 0x1F1D1C,         // [D] L* 12 — hot asphalt, still steaming, 5.4:1 vs base road
  freshDry: 0x332E2C,         // [S] potholes/Pot_hole_repair_vehicle_Common_Lane
  freshSheen: 0x5F5C5C,       // [S] the specular highlight riding on the wet fresh patch
  season1: 0x57585D,          // [S] Pothole_in_Villeray — a patch a few weeks old
  cured: 0x8C8C8C,            // [S] Sheffield patch quilt, blue cast neutralised — L* 58
  void: 0x242830,             // [S] Pothole_in_Villeray — the interior shadow of an open hole
  voidDeep: 0x14161B,         // [D] bottom of the hole
  waterLit: 0x876E52,         // [S] Pothole_in_Potomac_after_rain
  waterShade: 0x54412C,       // [S] same
  rim: 0x6E6A66,              // [D] the broken crumbling lip around a hole
});

export const MARKINGS = Object.freeze({
  white: 0xF1F3F0,            // [S] ct-t600 lane paint
  whiteWorn: 0xC8C8C4,        // [S] Rue_Saint-Denis_Montreal_06
  /**
   * Montreal centre line, LIFTED IN VALUE from the sampled #F1D24A.
   *
   * Round 8: "the yellow lane line separates by hue only, not by value, so it disappears in
   * greyscale and for colour-blind players." Measured, the sample sits at Y 0.647 linear
   * against lit asphalt's 0.377 — 1.72:1, below the 2:1 a value-only read needs. #F6DF63 is
   * Y 0.726, i.e. 1.93:1, and is still unmistakably a yellow centre line rather than cream.
   * This is an accessibility fix, not a rubric one; it costs nothing and it is measurable.
   */
  yellow: 0xF6DF63,           // [S+] Montreal centre line, value-lifted (sampled #F1D24A)
  yellowWorn: 0xCBB358,       // [D]
  crosswalk: 0xE8E9E4,        // [D]
  bikeBlue: 0x1E4E8C,         // [D] Montreal painted bike lane
});

export const KERB = Object.freeze({
  face: 0x9A968E,             // [D] cast concrete kerb, slightly warmer than sidewalk
  top: 0xAEAAA1,              // [D]
  shade: 0x6E6B66,            // [D]
  paintedYellow: 0xD8B441,    // [D] no-parking kerb paint
});

export const SIDEWALK = Object.freeze({
  concrete: 0x8F8F8D,         // [S] Avenue_du_Mont-Royal_05
  concreteLit: 0xACACA8,      // [D]
  concreteShade: 0x666663,    // [D]
  joint: 0x5E5E5C,            // [D] the scored expansion joints
  paver: 0xC4C6C1,            // [S] ct-t552 plaza paving — used for wider commercial frontage
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   4. MONTREAL MASONRY
   The single most important note: unpainted Montreal brick is NOT #B22222. It sampled
   between #A38C74 and #B9AA99 — a warm greyed brown at S≈0.25. Only PAINTED brick gets hot.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const BRICK = Object.freeze({
  /**
   * [D] What each brick MATERIAL is pinned to. Rubric `wide-establish` crit 7 wants brick to
   * land in the #A38C74–#DBAA90 family under key light and never exceed S = 0.40 unpainted;
   * these are the mean albedos that put it there. The per-brick tonal jitter in the texture
   * varies around them.
   */
  // Round 3 measured: rendered brick sat ~40% under the reference family and about 1.8x its
  // saturation cap. These are the same hues one step lighter and one step greyer, so a lit
  // wall lands inside #A38C74–#DBAA90 with S under 0.40 once the grade's 1.24 chroma lift
  // has been applied on top. Do not push the value further: `street-speed` criterion 3 needs
  // the ROAD to stay the brightest large surface, and the road is Y 0.37.
  redAlbedo: 0xB7A492,
  ochreAlbedo: 0xCBB89D,
  orangeAlbedo: 0xCAA492,
  /** Red-brown brick — the default Plateau triplex. */
  redLit: 0xB9AA99,           // [S] Avenue_du_Mont-Royal_29
  redMid: 0xA38C74,           // [S] same
  redShade: 0x484139,         // [S] same
  /** Orange-red brick — the warmer Mile End variant. */
  orangeLit: 0xDBAA90,        // [S] Avenue_du_Mont-Royal_36
  orangeMid: 0xC19173,        // [S] same
  orangeShade: 0x6B4E3C,      // [D]
  /** Buff / ochre brick. */
  ochreLit: 0xE2C8A7,         // [S] Mont-Royal / Saint-Hubert
  ochreMid: 0xBE9C68,         // [S] same
  ochreShade: 0x6E5B3D,       // [D]
  /** PAINTED brick — this is the only place brick is allowed to be saturated. */
  paintedRedLit: 0xC9493F,    // [S] Montreal_Plateau.JPG
  paintedRedShade: 0x992932,  // [S] same
  paintedBlue: 0x3E6E93,      // [D] the blue-painted triplex on the contact sheet
  paintedCream: 0xE0D3B4,     // [D]
  /** Mortar. Always lighter than the brick or the wall reads as one flat mass. */
  mortar: 0xC8C3B6,           // [D]
  mortarDark: 0x9A9488,       // [D]
});

export const STONE = Object.freeze({
  greystoneLit: 0xDCDAD2,     // [S] Avenue_du_Mont-Royal_36 — Montreal greystone is a WARM
  greystoneMidLit: 0xC8C1B6,  // [S] neutral limestone, NOT blue-grey concrete
  greystoneMid: 0xA7A193,     // [S] Rue_Saint-Denis_Montreal_06
  /** [D] What the greystone MATERIAL is pinned to, so the wall's mean lands on the sampled
   *  #A7A193 family under key light. The stone texture's own courses vary around this. */
  // Lifted with the brick, and kept WARM (R > B by 20) — the round-3 note was that greystone
  // read cool and about 35 luma points dark against the sampled #A7A193 family.
  greystoneAlbedo: 0xBEB7A6,
  greystoneShade: 0x928977,   // [S] same
  greystoneDeep: 0x847B6B,    // [S] same
  sill: 0xB7B2A6,             // [D] cut-stone window sills and lintels
  slate: 0x504B44,            // [S] mansard slate / dark trim
});

export const RENDER_STUCCO = Object.freeze({
  white: 0xFCF9F9,            // [S] ct-t600 white building
  pale: 0xC0BFBE,             // [S] ct-t600 pale stucco
  warmGrey: 0xAAA89C,         // [S] ct-t600 warm grey
  teal: 0x88A6B3,             // [S] ct-t600 teal accent — CT loves a teal building
  terracotta: 0x763A27,       // [S] ct-t600 terracotta accent
  cream: 0xE7DCC4,            // [D]
});

export const TRIM = Object.freeze({
  /**
   * PALE LIMESTONE. `building.trim` is the lintel / sill / stringcourse band across a
   * Plateau facade, and it only reads as one if it sits ABOVE the wall in value. The old
   * #6D6457 was a dark pressed-metal cornice colour and could never be a lintel; that
   * colour now lives on `building.cornice`.
   */
  lintel: 0xC9C3B4,           // [D]
  cornice: 0x6E6558,          // [D] pressed-metal cornice, the Plateau signature
  corniceLight: 0x9A9184,     // [D]
  frameWhite: 0xEDE9E0,       // [D] window frames
  frameGreen: 0x2F5741,       // [D] the dark-green painted woodwork you see everywhere
  frameRed: 0x8E3129,         // [D]
  frameBlue: 0x2C4A72,        // [D]
  doorWood: 0x53331E,         // [D]
});

export const WINDOW = Object.freeze({
  glassDay: 0x2B3946,         // [D] dark reflective slab — reads as a hole in the wall
  glassDayLit: 0x54707F,      // [D] the sky reflection catching a pane
  glassNight: 0x100D0A,       // [D] an unlit apartment at night
  litWarm: 0xFFCB73,          // [D] someone is home
  /**
   * A TV or a fluorescent kitchen, and the single largest source of COOL in a night frame.
   *
   * Round 10 measured 95.3% of our colourful lit pixels inside one 60-degree hue wedge and
   * 0.2% blue. #C6DCF2 was not part of the answer: at HSV S 0.18 it does not clear the 0.25
   * threshold the critic counted with, so every lit pane in the city was scored as "warm or
   * neutral" no matter how many were nominally cool. A 6500 K screen seen through glass
   * against a sodium-lit brick wall genuinely reads this blue — the eye white-balances to
   * the sodium, which pushes everything else cold. S 0.36.
   */
  litCool: 0x9EC8F5,          // [D] a TV / fluorescent kitchen
  litRetail: 0xFFE9C0,        // [D] shopfront at night
  mullion: 0x2A2622,          // [D]
});

export const ROOF = Object.freeze({
  /**
   * [D] What `building.roof` is pinned to. A flat tar-and-gravel roof is a DARK surface and
   * from an establishing height it is a large one, so it decides the top half of the value
   * hierarchy. Round 3's roofs came out as the brightest surface in frame, inverting it.
   */
  albedo: 0x4A463E,
  tar: 0x3A3833,              // [D] flat tar-and-gravel roof — the Montreal default
  gravel: 0x726B5E,           // [D]
  metal: 0x6C7276,            // [D]
});

/**
 * DOWNTOWN TOWERS — the curtain-wall quadrant. Added for the city expansion.
 *
 * Blue-green glass is a genuine Montreal identifier, not a generic-city choice: Place Ville
 * Marie's cruciform tower is the anchor of the skyline and its glazing reads distinctly
 * blue-green against the Plateau's red/ochre brick. Keeping the towers in that family is
 * what stops the downtown quadrant looking like Anywhere, USA (§10 "Montreal legibility").
 *
 * VALUE HIERARCHY (§10): towers are BACKGROUND. They are the largest objects in a wide shot
 * and must sit BELOW the road ribbon and well below cones/potholes in contrast, or they
 * steal the read at 90 km/h. Every value here is deliberately mid-to-dark.
 */
export const TOWER = Object.freeze({
  /** [D] Curtain-wall pane, daylight — a dark blue-green slab that reads as a hole in the wall. */
  glassDay: 0x33565C,
  /** [D] The sky catching a pane. The only bright note, and only on a wedge of each pane. */
  glassDayLit: 0x8FBAB8,
  /** [D] Shadowed / north-face pane, so a tower face is never one flat plate. */
  glassDeep: 0x1E3438,
  /**
   * [D] Anodized aluminium mullion. LIGHTER than WINDOW.mullion (#2A2622): a residential
   * window frame is dark painted wood, a curtain-wall mullion is bare metal, and that
   * lattice is what makes the surface read as "tower" rather than "big brick wall".
   */
  mullion: 0x8A9095,
  /**
   * [D] Office fluorescent — cool and faintly green. Deliberately NOT WINDOW.litWarm: an
   * office floor at night is not somebody's living room, and round 10's finding that 95.3%
   * of lit pixels sat in one warm hue wedge makes a large cool source valuable in its own
   * right. See WINDOW.litCool for the measurement.
   */
  litOffice: 0xD8E8E2,
  /** [D] The few floors with warm lamps on — stops the lit grid reading as one flat colour. */
  litOfficeWarm: 0xFFD9A0,
  /** [D] Opaque spandrel band between floors: dark anodized panel. Reads as the floor line. */
  spandrel: 0x2E3236,
  /** [D] Street-level podium: urban-commercial concrete, greyer and cooler than greystone. */
  podium: 0x9A968C,
});

export const METAL = Object.freeze({
  staircaseBlack: 0x24262A,   // [D] the wrought-iron exterior staircase. Non-negotiable identifier.
  staircaseGrey: 0x51565C,    // [D]
  staircaseWhite: 0xD6D4CE,   // [D] plenty of them are painted white
  galvanised: 0x9AA0A4,       // [D]
  rust: 0x7A4526,             // [D]
  chrome: 0xC8CDD2,           // [D]
  darkSteel: 0x35393D,        // [D]
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   5. CONES — the highest-contrast object in the game
   Note the hue swing across the form: the lit face is ~hue 20°, the shaded side crushes to
   ~hue 8° and loses 30% value. A flat #FF6600 cone reads as plastic and dies at distance.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const CONE = Object.freeze({
  litRim: 0xF88849,           // [S] cones/Cône_de_signalisation — rim-lit edge
  lit: 0xFC6523,              // [S] lit face
  mid: 0xD74611,              // [S] mid
  shade: 0xAB2419,            // [S] shade
  deepShade: 0x6D291C,        // [S] deep shade / ground contact
  bandLit: 0xEDEDEB,          // [S] reflective band, lit
  bandShade: 0xBEB8C0,        // [S] reflective band, shade
  bandGlow: 0xFFFFFF,         // [D] retroreflective return when headlights hit it at night
  base: 0x2E2E2E,             // [S] rubber base
  dirty: 0x9A5A32,            // [D] a cone that has been kicked down the street for a month
});

export const BARREL = Object.freeze({
  orange: 0xE8601F,           // [D] the big plastic drum barricade
  white: 0xE4E2DC,            // [D]
  base: 0x3A3A3A,             // [D]
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   6. VEHICLES
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const TRUCK = Object.freeze({
  /** Ville de Montréal works-truck livery: safety yellow body, white cab band, blue crest. */
  yellow: 0xF2B01E,           // [D] municipal safety yellow
  yellowShade: 0xB07B12,      // [D]
  white: 0xEFEDE6,            // [D]
  blue: 0x0B4C8C,             // [D] the Ville de Montréal blue
  // [D] Chalky, not black. Round 3's hero truck was the darkest mass in the frame and read
  // as a hole at frame centre — the exact inverse of the reference's value hierarchy.
  chassis: 0x2E3034,
  bed: 0x53565A,              // [D] the hot-box / hopper
  hotBox: 0x8A3A16,           // [D] heat-scorched asphalt hopper
  glass: 0x24333F,            // [D]
  beaconAmber: 0xFFA419,      // [D] roof beacon
  headlight: 0xFFF6E0,        // [D]
  brake: 0xFF2A16,            // [D]
  reverse: 0xFFFFFF,          // [D]
  tyre: 0x1C1C1E,             // [D]
  rim: 0x8E9398,              // [D]
});

/** Traffic paints — deliberately a bit desaturated so the truck's yellow stays the hero. */
export const TRAFFIC = Object.freeze({
  paints: Object.freeze([
    0xB8BDC2,  // silver — the most common car in Montreal by a mile
    0xE8E9E7,  // white
    0x2E3238,  // charcoal
    0x8E1F1F,  // dark red
    0x1E4370,  // navy
    0x3E6B4A,  // muted green
    0x9C6A2A,  // bronze
    0x5A5F66,  // gunmetal
  ]),
  glass: 0x27333C,            // [D]
  taxiWhite: 0xF0EFE9,        // [D] Montreal taxis are white with a roof sign
  busBlue: 0x1B3E6E,          // [D] STM bus
  cyclistFrame: 0x1F2A33,     // [D]
  bixiRed: 0xC0362C,          // [D] BIXI bike red
});

export const CHAR = Object.freeze({
  skin: Object.freeze([0xE0B18C, 0xC38B62, 0x8B5A3C, 0x5E3B27, 0xF0CBA8]),
  hiVis: 0xC8E62A,            // [D] the works-crew vest — should read instantly
  cloth: Object.freeze([0x2B3440, 0x6B4A3A, 0x8C2F2F, 0x3C5A46, 0x252528, 0xB0AFA8]),
  hair: Object.freeze([0x2A2320, 0x4A3524, 0x8A6A3A, 0x6E6E70]),
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   7. WEATHER SURFACES
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const SNOW = Object.freeze({
  lit: 0xE0E4E6,              // [S] winter/Montreal_-_Plateau__day_of_snow
  bright: 0xF2F4F5,           // [D] direct-lit crown of a snowbank
  shadow: 0xABB7C0,           // [S] note the blue shift: B exceeds R by 21
  deepShadow: 0x8C99A4,       // [D]
  dirty: 0x8D715C,            // [S] March 2017 blizzard — snowbank dirt stain
  dirtyGrey: 0xA3A099,        // [D] road-splash grey on the kerbside bank
  sodiumLit: 0xE1CA98,        // [S] snowbank under a sodium lamp at night
  sodiumDirty: 0x734A2D,      // [S] dirty snowbank, night
});

export const SLUSH = Object.freeze({
  /**
   * Rubric `winter-slush` crit 1 caps saturation at 0.15 and the round-3 critic measured our
   * carriageway at S = 0.262 with B-R = +32 — an over-blue road that then read as the
   * DARKEST large surface in frame. Open slush is a light near-neutral; the blue shift is
   * reserved for shadowed snow and the wet ruts.
   */
  tracked: 0xC2C7C9,          // [S] tracked slush, pulled toward neutral (B-R = +7, S = 0.031)
  wet: 0xB6BCC0,              // [S] wetter slush
  rut: 0x5F6A72,              // [D] the dark wheel rut cut down to the asphalt — the ONLY
                              //     place the strong blue shift is allowed on the roadway
  spray: 0xD8DEE3,            // [D] what comes off a tyre
  edge: 0x8E9298,             // [D]
});

export const WATER = Object.freeze({
  puddleDay: 0x54606B,        // [D] a puddle reflecting a blue sky reads dark and cool
  puddleNight: 0x2A2416,      // [D]
  wetAsphaltDay: 0x5E6165,    // [D] wet asphalt is DARKER and glossier than dry
  wetAsphaltNight: 0x1A1710,  // [D]
  sodiumStreak: 0xAF8739,     // [S] wet road, amber reflection streak under a lamp
  sodiumStreakFar: 0x9D782D,  // [S] further from the lamp
  rain: 0xBFCEDB,             // [D] a rain streak lit from behind
  splash: 0xD5DEE4,           // [D]
  spray: 0xC3CED6,            // [D]
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   8. SIGNAGE / NEON  — Montreal dépanneur frontage at night
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const SIGN = Object.freeze({
  panelWhite: 0xE9E6DD,       // [D] backlit plastic box sign
  panelRed: 0xB4271F,         // [D]
  panelBlue: 0x1B4C86,        // [D]
  panelGreen: 0x1E6B45,       // [D]
  panelYellow: 0xE8B71C,      // [D]
  panelBlack: 0x191A1C,       // [D]
  frame: 0x3A3D40,            // [D]
  /** Neon tube hues. These go into an emissive slot and get bloomed. */
  neonRed: 0xFF2E3A,
  neonPink: 0xFF3FA0,
  neonOrange: 0xFF7A18,
  neonAmber: 0xFFB020,
  neonGreen: 0x35F58A,
  neonCyan: 0x2EE8F0,
  neonBlue: 0x3E7BFF,
  neonWhite: 0xFFF4E2,
  /** Sampled from a real Saint-Denis winter decoration — a genuinely Montreal green globe. */
  globeGreen: 0x46B68F,       // [S]
  /** Traffic signal lenses. */
  signalRed: 0xFF2B1C,
  signalAmber: 0xFFA51E,
  signalGreen: 0x27E060,
  /** `arrêt` stop sign. */
  stopRed: 0xC0201C,
  stopWhite: 0xF2EFE8,
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   9. VEGETATION
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Painted and bare timber — hoardings, ruelle fences, balcony decking, the plywood sheets
 * every Montreal worksite is wrapped in. Requested by `world`.
 */
export const WOOD = Object.freeze({
  bareLit: 0xA8926F,          // [D] weathered softwood
  bareShade: 0x6E5C42,        // [D]
  paintedGrey: 0x8E8B82,      // [D] the standard hoarding grey
  paintedGreen: 0x3E5B44,     // [D]
  plywood: 0xC0A173,          // [D] fresh sheet ply — reads warm against greystone
  creosote: 0x3B2C22,         // [D] pole / sleeper timber
});

export const FLORA = Object.freeze({
  grassLit: 0x4F7528,         // [S] ct-t528 — Crazy Taxi's grass is REALLY green. Keep it.
  grassShade: 0x31590F,       // [S] same
  leafLit: 0x63913A,          // [D]
  leafMid: 0x466B27,          // [D]
  leafShade: 0x2A421A,        // [D]
  bark: 0x4A423A,             // [D]
  barkLight: 0x6E6558,        // [D]
  bareBranch: 0x453C33,       // [D] winter — the Plateau is all bare trees half the year
  planter: 0x4A4E45,          // [D]
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   10. FX
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const FX = Object.freeze({
  asphaltHot: 0x2A2320,       // [D] the sweeping arc of hot mix
  asphaltHotGlow: 0xFF7A2E,   // [D] the glowing edge on freshly laid mix
  steam: 0xD9D5CC,            // [D]
  smoke: 0x6E6A64,            // [D]
  dust: 0xB6AC99,             // [D]
  sparkCore: 0xFFF0C0,        // [D]
  sparkTail: 0xFF8A20,        // [D]
  tyreMark: 0x1A1817,         // [D]
  impactFlash: 0xFFF2CC,      // [D]
  comboFlash: 0xFFD21E,       // [D]
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   11. HUD ACCENTS
   Sampled from the Crazy Taxi HUD (arcade confidence) and the PowerWash Simulator HUD
   (completion-meter legibility). `ui` owns layout; these are the only legal colours.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const HUD = Object.freeze({
  timerYellow: 0xFFF200,      // [S] #FAFC1A sampled; the green cast is composite video
  timerYellowMid: 0xE4EA44,   // [S] antialias ring
  outlineBlack: 0x0C120D,     // [S] outline on ALL Crazy Taxi HUD type, ~0.6% of screen height
  plateCream: 0xF4F2EA,       // [S] #E8EBEE marquee tile face, warmed
  plateFrameRed: 0xA53639,    // [S] marquee frame + end bulbs
  hotRed: 0xE00030,           // [S-derived] "STOP AT THE MARK." crimson
  slowRed: 0xC50904,          // [S] the "SLOW" plate
  fareNavy: 0x101428,         // [S] fare-plate digit navy
  fareNavyMid: 0x2B2D52,      // [S]
  plateSilver: 0xB5B9C8,      // [S] plate bevel
  arrowGreen: 0x0BCE38,       // [S] the big directional chevron, lit face
  arrowGreenHi: 0x17E653,     // [S] highlight edge
  arrowGreenLo: 0x08AC32,     // [S] shaded face — the arrow is EXTRUDED, not flat
  promptGreen: 0x24F416,      // [S] "SPEEDY" prompt
  promptGreenOutline: 0x0C6409,// [S]
  rankPlate: 0xE9EAE7,        // [S] split-flap ranking plate
  rankFrame: 0x11110F,        // [S]
  rankGold: 0x929071,         // [S] "RANKING" header lettering
  /** PowerWash Simulator completion-meter grammar. */
  pwsNavyDeep: 0x0C3352,      // [S]
  pwsNavy: 0x103049,          // [S]
  pwsSlate: 0x2F5373,         // [S]
  pwsBarEmpty: 0x1D4163,      // [S]
  pwsAzure: 0x0070CE,         // [S] cash plate
  pwsAzureBar: 0x0361B5,      // [S] tool bar
  pwsOrange: 0xC46424,        // [S] material pill
  pwsWhite: 0xFFFFFF,         // [S] text / meter fill
  /** Our own accents, built to sit with the two above. */
  coneOrange: 0xFC6523,       // shares the cone's lit face so the HUD and the world agree
  timeExtend: 0x3CE87A,
  danger: 0xFF3123,
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   12. SHADOW / GRADE ANCHORS
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const GRADE = Object.freeze({
  /** [S] ct-t456 x380 y700 — the cast shadow under the taxi. Shadows are DARK and DEFINITE. */
  castShadow: 0x222823,
  /** Deepest legal black in the frame. Nothing crushes past this. */
  floor: 0x0B0D10,
  /** Brightest legal non-emissive white. */
  ceiling: 0xF7F8F5,
});

/* ══════════════════════════════════════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════════════════════════════════════ */

const _colorCache = new Map();

/**
 * Cached THREE.Color for a palette hex. Shared instance — DO NOT MUTATE the result.
 * Use `col(hex).clone()` if you need to change it.
 */
export function col(hex) {
  let c = _colorCache.get(hex);
  if (!c) { c = new THREE.Color(hex); _colorCache.set(hex, c); }
  return c;
}

/** Fresh THREE.Color from a palette hex — safe to mutate. */
export function color(hex) { return new THREE.Color(hex); }

/** sRGB hex → {r,g,b} in 0..255. For canvas-2D texture generation. */
export function rgb255(hex) {
  return { r: (hex >> 16) & 255, g: (hex >> 8) & 255, b: hex & 255 };
}

/** sRGB hex → 'rgb(r,g,b)' for canvas fillStyle. */
export function css(hex, alpha = 1) {
  const r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

/** Blend two sRGB hexes in 8-bit space. Cheap and good enough for texture speckle. */
export function mixHex(a, b, t) {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const r = (ar + (br - ar) * t) | 0;
  const g = (ag + (bg - ag) * t) | 0;
  const bl = (ab + (bb - ab) * t) | 0;
  return (r << 16) | (g << 8) | bl;
}

/** Multiply an sRGB hex's value (not a true HSL op — fast and stable). */
export function shadeHex(hex, k) {
  const r = Math.min(255, Math.max(0, ((hex >> 16) & 255) * k)) | 0;
  const g = Math.min(255, Math.max(0, ((hex >> 8) & 255) * k)) | 0;
  const b = Math.min(255, Math.max(0, (hex & 255) * k)) | 0;
  return (r << 16) | (g << 8) | b;
}

/** Relative luminance of an sRGB hex, 0..1. Used to audit value separation. */
export function luma(hex) {
  const s = (v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * s((hex >> 16) & 255) + 0.7152 * s((hex >> 8) & 255) + 0.0722 * s(hex & 255);
}

/** WCAG-style contrast ratio between two palette hexes. */
export function contrast(a, b) {
  const la = luma(a), lb = luma(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The whole palette in one frozen namespace, for anyone who wants a single import.
 * `import { PALETTE } from '../materials/palette.js'`
 */
export const PALETTE = Object.freeze({
  SKY, LIGHT, ROAD, PATCH, MARKINGS, KERB, SIDEWALK,
  BRICK, STONE, RENDER_STUCCO, TRIM, WINDOW, ROOF, METAL,
  CONE, BARREL, TRUCK, TRAFFIC, CHAR,
  SNOW, SLUSH, WATER, SIGN, FLORA, WOOD, FX, HUD, GRADE,
});

export default PALETTE;
